import React, { useState } from 'react';
import { 
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    TextField,
    Box,
    Alert,
    CircularProgress,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    Stepper,
    Step,
    StepLabel,
    Divider,
    Tooltip
} from '@mui/material';
import { Visibility, VisibilityOff, Info } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { GoogleLogin } from '@react-oauth/google';
import scanApi from '../services/api';

// Separate validation schemas for login and registration
const loginValidationSchema = Yup.object({
    email: Yup.string()
        .email('Invalid email address')
        .required('Email is required')
});

const pinValidationSchema = Yup.object({
    pin: Yup.string()
        .matches(/^[0-9]{6}$/, 'PIN must be 6 digits')
        .required('PIN is required')
});

const registrationValidationSchema = Yup.object({
    username: Yup.string()
        .min(3, 'Username must be at least 3 characters')
        .max(50, 'Username too long')
        .required('Username is required'),
    email: Yup.string()
        .email('Invalid email address')
        .required('Email is required'),
    password: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .matches(/[a-z]/, 'Must contain at least one lowercase letter')
        .matches(/[A-Z]/, 'Must contain at least one uppercase letter')
        .matches(/[0-9]/, 'Must contain at least one number')
        .matches(/[^a-zA-Z0-9]/, 'Must contain at least one special character')
        .required('Password is required'),
    name: Yup.string().required('First name is required'),
    surname: Yup.string().required('Last name is required'),
    street_address: Yup.string().required('Street address is required'),
    street_number: Yup.string().required('Street number is required'),
    postal_code: Yup.string()
        .matches(/^[0-9]{5}$/, 'Invalid postal code')
        .required('Postal code is required'),
    city: Yup.string().required('City is required'),
    country: Yup.string().required('Country is required'),
    phone_number: Yup.string()
        .matches(/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/, 'Invalid phone number')
        .required('Phone number is required'),
    scan_targets: Yup.array().of(
        Yup.object().shape({
            domain: Yup.string()
                .matches(/^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/, 'Invalid domain')
                .required('Domain is required')
        })
    ).min(1, 'At least one target domain is required')
});

// Steps del form
const steps = ['Credentials', 'Personal Info', 'Company Info', 'Target Domains'];

const LoginRegisterModal = ({ open, onClose, onLogin, onRegister, isLoading, error, initialView = 'login' }) => {
    const [isLoginView, setIsLoginView] = useState(initialView === 'login');
    const [activeStep, setActiveStep] = useState(0);
    const [showPassword, setShowPassword] = useState(false);
    const [showPinInput, setShowPinInput] = useState(false);
    const [statusMessage, setStatusMessage] = useState({ type: null, message: '' });
    const [successMessage, setSuccessMessage] = useState(null);

    const handleAutoCreateAccount = async (email) => {
        try {
            // Genera una password casuale sicura
            const tempPassword = Math.random().toString(36).slice(-12) + 
                               Math.random().toString(36).toUpperCase().slice(-4) + 
                               '@' + Math.floor(Math.random() * 1000);
            
            // Crea il nuovo account
            await scanApi.register({
                email: email,
                password: tempPassword,
                username: email.split('@')[0],
                name: 'User',
                surname: email.split('@')[0],
                street_address: 'TBD',
                street_number: '1',
                postal_code: '00000',
                city: 'TBD',
                country: 'TBD',
                phone_number: '+000000000000'
            });

            return true;
        } catch (error) {
            console.error('Error creating account:', error);
            return false;
        }
    };

    // Reset form when modal is closed
    const handleClose = () => {
        formik.resetForm();
        setStatusMessage({ type: null, message: '' });
        setShowPassword(false);
        setShowPinInput(false);
        setActiveStep(0);
        onClose();
    };

    // Add debug logging
    const debugLog = (message, data) => {
        console.log(`[LoginRegisterModal] ${message}:`, data);
    };

    // Update handleFormReset to not change view on close
    const handleFormReset = () => {
        debugLog('Resetting form and switching view', { 
            currentView: isLoginView ? 'login' : 'register',
            newView: isLoginView ? 'register' : 'login'
        });
        
        setIsLoginView(!isLoginView);
        setActiveStep(0);
        setShowPinInput(false);
        formik.resetForm();
        setStatusMessage({ type: null, message: '' });
        setShowPassword(false);
    };

    // Handle Google login success
    const handleGoogleSuccess = async (credentialResponse) => {
        try {
            debugLog('Google login success', {
                hasCredential: !!credentialResponse.credential,
                credentialLength: credentialResponse.credential?.length
            });

            // Decodifica il token JWT per il logging
            const tokenParts = credentialResponse.credential.split('.');
            const payload = JSON.parse(atob(tokenParts[1]));
            
            debugLog('Google token payload', {
                email: payload.email,
                name: payload.name,
                emailVerified: payload.email_verified
            });

            // Verifica che l'email sia verificata
            if (!payload.email_verified) {
                setStatusMessage({
                    type: 'error',
                    message: 'È richiesta un\'email verificata per il login'
                });
                return;
            }

            const response = await scanApi.loginWithGoogle(credentialResponse.credential);

            debugLog('Login response', {
                success: !!response,
                hasToken: !!response?.token,
                requiresApproval: response?.requiresApproval
            });

            if (response?.requiresApproval) {
                setStatusMessage({
                    type: 'info',
                    message: 'Account in attesa di approvazione. Sarai notificato quando il tuo account sarà attivato.'
                });
                return;
            }

            setSuccessMessage({
                title: 'Login Effettuato!',
                message: 'Accesso effettuato con successo tramite Google. Reindirizzamento in corso...',
                type: 'success',
                autoClose: true
            });

            // Chiudi il modal e reindirizza dopo un breve delay
            setTimeout(() => {
                setSuccessMessage(null);
                onClose();
                window.location.href = '/scan';
            }, 1500);
        } catch (err) {
            debugLog('Google login error', {
                error: err.message,
                type: err.name,
                response: err.response?.data
            });

            let errorMessage = 'Errore durante il login con Google. Riprova.';
            let errorType = 'error';

            if (err.message === 'ACCOUNT_PENDING_APPROVAL') {
                errorMessage = 'Account in attesa di approvazione';
                errorType = 'info';
            } else if (err.response?.status === 400) {
                errorMessage = err.response.data?.error || 'Email non valida';
            } else if (err.response?.status === 401) {
                errorMessage = 'Credenziali non valide';
            }

            setStatusMessage({
                type: errorType,
                message: errorMessage
            });
        }
    };

    // Handle Google login error
    const handleGoogleError = (error) => {
        debugLog('Google login error', {
            error: error,
            message: error?.message || 'Unknown error'
        });
        
        setStatusMessage({
            type: 'error',
            message: 'Login con Google fallito. Riprova più tardi.'
        });
    };

    // Configurazione Formik
    const formik = useFormik({
        initialValues: {
            email: '',
            pin: '',
            username: '',
            password: '',
            name: '',
            surname: '',
            street_address: '',
            street_number: '',
            postal_code: '',
            city: '',
            country: '',
            phone_number: '',
            scan_targets: [{ domain: '' }]
        },
        validationSchema: isLoginView 
            ? (showPinInput ? pinValidationSchema : loginValidationSchema)
            : registrationValidationSchema,
        onSubmit: async (values) => {
            try {
                debugLog('Form submission started', { isLoginView, values });
                
                if (isLoginView) {
                    if (!showPinInput) {
                        try {
                            // Prova a richiedere il PIN
                            await scanApi.requestEmailPin(values.email);
                            setShowPinInput(true);
                            setStatusMessage({
                                type: 'info',
                                message: 'A secure PIN has been sent to your email'
                            });
                        } catch (error) {
                            if (error.response?.status === 404) {
                                // Email non trovata, creiamo un nuovo account
                                const created = await handleAutoCreateAccount(values.email);
                                if (created) {
                                    // Richiedi il PIN per il nuovo account
                                    await scanApi.requestEmailPin(values.email);
                                    setShowPinInput(true);
                                    setStatusMessage({
                                        type: 'success',
                                        message: 'Account created! Check your email for the PIN'
                                    });
                                } else {
                                    setStatusMessage({
                                        type: 'error',
                                        message: 'Failed to create account. Please try again.'
                                    });
                                }
                            } else {
                                throw error;
                            }
                        }
                    } else {
                        // Verifica PIN
                        debugLog('Verifying PIN', { email: values.email, pin: values.pin });
                        const response = await scanApi.verifyEmailPin(values.email, values.pin);

                    setSuccessMessage({
                            title: 'Welcome!',
                            message: 'Login successful. Redirecting...',
                        type: 'success',
                        autoClose: true
                    });

                        // Chiudi il modal e reindirizza dopo un breve delay
                    setTimeout(() => {
                        setSuccessMessage(null);
                        onClose();
                            window.location.href = '/scan';
                    }, 1500);
                    }
                } else {
                    debugLog('Attempting registration', { 
                        username: values.username,
                        email: values.email
                    });
                    
                    const registrationData = {
                        username: values.username,
                        email: values.email,
                        password: values.password,
                        name: values.name,
                        surname: values.surname,
                        street_address: values.street_address,
                        street_number: values.street_number,
                        postal_code: values.postal_code,
                        city: values.city,
                        country: values.country,
                        phone_number: values.phone_number,
                        scan_targets: values.scan_targets
                    };

                    debugLog('Registration data prepared', registrationData);
                    
                    const response = await onRegister(registrationData);
                    debugLog('Registration response', response);

                    if (response?.requiresApproval) {
                        setSuccessMessage({
                            title: 'Registration Complete!',
                            message: 'Your account has been created successfully and is pending administrator approval. You will be notified when your account is activated.',
                            type: 'success',
                            autoClose: false
                        });
                    }
                }
            } catch (err) {
                debugLog('Form submission error', err);
                
                let errorMessage;
                if (isLoginView) {
                    switch (err.response?.status) {
                        case 401:
                            errorMessage = 'Invalid PIN';
                            break;
                        case 403:
                            errorMessage = 'Account pending approval';
                            break;
                        case 429:
                            errorMessage = 'Too many attempts. Please try again later';
                            break;
                        default:
                            errorMessage = err.response?.data?.error || 'Error verifying PIN';
                    }
                } else {
                    errorMessage = err.response?.data?.error || 'Error sending PIN';
                }
                
                setStatusMessage({
                    type: 'error',
                    message: errorMessage
                });
                
                debugLog('Error message set', errorMessage);
            }
        },
        validateOnChange: true,
        validateOnBlur: true
    });

    // Gestione steps
    const handleNext = () => {
        debugLog('Moving to next step', { 
            currentStep: activeStep,
            isValid: isStepValid() 
        });
        
        if (isStepValid()) {
            setActiveStep((prevStep) => prevStep + 1);
        }
    };

    const handleBack = () => {
        debugLog('Moving to previous step', { currentStep: activeStep });
        setActiveStep((prevStep) => prevStep - 1);
    };

    const isStepValid = () => {
        const stepFields = {
            0: ['username', 'email', 'password'],
            1: ['name', 'surname'],
            2: ['street_address', 'street_number', 'postal_code', 'city', 'country', 'phone_number'],
            3: ['scan_targets']
        };

        const currentStepFields = stepFields[activeStep];
        debugLog('Checking step validity', { 
            step: activeStep, 
            fields: currentStepFields 
        });

        const isValid = currentStepFields.every(field => {
            if (field === 'scan_targets') {
                const isTargetsValid = formik.values.scan_targets.every(target => 
                    target.domain && target.domain.length > 0
                );
                debugLog('Scan targets validation', { isValid: isTargetsValid });
                return isTargetsValid;
            }

            const fieldValue = formik.values[field];
            const fieldError = formik.errors[field];
            const isFieldValid = fieldValue && fieldValue.length > 0 && !fieldError;
            
            debugLog('Field validation', { 
                field, 
                value: fieldValue, 
                error: fieldError,
                isValid: isFieldValid 
            });
            
            return isFieldValid;
        });

        debugLog('Step validation result', { step: activeStep, isValid });
        return isValid;
    };

    // Show success dialog
    if (successMessage) {
        return (
            <Dialog 
                open={true} 
                onClose={() => {
                    if (!successMessage.autoClose) {
                        setSuccessMessage(null);
                        onClose();
                    }
                }}
                PaperProps={{
                    sx: {
                        bgcolor: '#0A1429',
                        color: 'white',
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'rgba(96,165,250,0.1)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                        minWidth: 300,
                        maxWidth: 400
                    }
                }}
            >
                <DialogContent sx={{ textAlign: 'center', p: 4 }}>
                    <Alert 
                        severity="success" 
                        sx={{ 
                            mb: 2,
                            bgcolor: 'rgba(46,125,50,0.1)',
                            color: 'white',
                            '& .MuiAlert-icon': {
                                color: '#4caf50'
                            },
                            border: '1px solid',
                            borderColor: 'rgba(76,175,80,0.2)',
                            borderRadius: 2,
                            '& .MuiAlert-message': {
                                width: '100%'
                            }
                        }}
                    >
                        <Typography variant="h6" gutterBottom sx={{ 
                            color: '#4caf50',
                            fontWeight: 600,
                            textShadow: '0 0 10px rgba(76,175,80,0.3)'
                        }}>
                            {successMessage.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                            {successMessage.message}
                        </Typography>
                    </Alert>
                    {!successMessage.autoClose && (
                        <Button 
                            fullWidth 
                            variant="contained" 
                            onClick={() => {
                                setSuccessMessage(null);
                                onClose();
                            }}
                            sx={{ 
                                mt: 2,
                                bgcolor: '#4caf50',
                                '&:hover': {
                                    bgcolor: '#43a047'
                                }
                            }}
                        >
                            Close
                        </Button>
                    )}
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="xs"
            PaperProps={{
                sx: {
                    bgcolor: 'rgba(10, 20, 41, 0.95)',
                    color: 'white',
                    borderRadius: '24px',
                    overflow: 'hidden',
                    width: '440px',
                    backgroundImage: `
                        radial-gradient(circle at 100% 0%, rgba(94,163,247,0.12), transparent 400px),
                        radial-gradient(circle at 0% 100%, rgba(59,130,246,0.12), transparent 400px)
                    `,
                    backdropFilter: 'blur(20px)',
                    border: '1px solid',
                    borderColor: 'rgba(96,165,250,0.15)',
                    boxShadow: `
                        0 0 0 1px rgba(96,165,250,0.1),
                        0 8px 32px rgba(0,0,0,0.5),
                        0 20px 64px rgba(96,165,250,0.1)
                    `
                }
            }}
        >
            <Box sx={{ 
                p: 4,
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '100%',
                    background: 'linear-gradient(180deg, rgba(96,165,250,0.03) 0%, transparent 100%)',
                    pointerEvents: 'none'
                }
            }}>
                <Box sx={{ 
                    position: 'relative',
                        textAlign: 'center', 
                    mb: 4
                }}>
                    {/* Logo/Icon */}
                    <Box sx={{ 
                        width: '72px',
                        height: '72px',
                        margin: '0 auto 20px',
                        background: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
                        borderRadius: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 12px 36px rgba(96,165,250,0.3)',
                        position: 'relative',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            inset: '-1px',
                            background: 'linear-gradient(135deg, rgba(96,165,250,0.5), rgba(59,130,246,0.5))',
                            borderRadius: 'inherit',
                            padding: '1px',
                            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                            WebkitMaskComposite: 'xor',
                            maskComposite: 'exclude'
                        }
                    }}>
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                            <path d="M12 2L20 7V17L12 22L4 17V7L12 2Z" 
                                  stroke="white" 
                                  strokeWidth="2"
                                  fill="rgba(255,255,255,0.1)" />
                            <path d="M12 6L16 8.5V13.5L12 16L8 13.5V8.5L12 6Z" 
                                  stroke="white" 
                                  strokeWidth="2"
                                  fill="rgba(255,255,255,0.2)" />
                        </svg>
                    </Box>

                    <Typography 
                        variant="h4" 
                        sx={{ 
                        fontWeight: 700,
                            fontSize: '2.2rem',
                            background: 'linear-gradient(135deg, #FFFFFF 0%, #A5B4FC 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            mb: 1.5,
                        fontFamily: '"Orbitron", sans-serif',
                        letterSpacing: '0.5px'
                    }}
                >
                        {showPinInput ? 'Enter PIN' : 'Welcome'}
                    </Typography>
                    <Typography 
                        variant="body1" 
                                sx={{ 
                                        color: 'rgba(255,255,255,0.7)',
                            fontSize: '1rem',
                            maxWidth: '300px',
                            margin: '0 auto',
                            lineHeight: 1.6
                        }}
                    >
                        {showPinInput 
                            ? 'Enter the secure PIN code sent to your email'
                            : 'Sign in or create an account with your email'}
                    </Typography>
                </Box>

                <DialogContent sx={{ p: 0 }}>
                    {statusMessage.type && (
                        <Alert 
                            severity={statusMessage.type}
                            sx={{ 
                                mb: 3,
                                bgcolor: statusMessage.type === 'error' 
                                    ? 'rgba(211,47,47,0.1)' 
                                    : statusMessage.type === 'info'
                                    ? 'rgba(96,165,250,0.1)'
                                    : 'rgba(46,125,50,0.1)',
                                color: 'white',
                                '& .MuiAlert-icon': {
                                    color: statusMessage.type === 'error' 
                                        ? '#f44336' 
                                        : statusMessage.type === 'info'
                                        ? '#60A5FA'
                                        : '#4caf50'
                                },
                                border: '1px solid',
                                borderColor: statusMessage.type === 'error'
                                    ? 'rgba(211,47,47,0.2)'
                                    : statusMessage.type === 'info'
                                    ? 'rgba(96,165,250,0.2)'
                                    : 'rgba(46,125,50,0.2)',
                                borderRadius: '12px',
                                '& .MuiAlert-message': {
                                    fontSize: '0.95rem'
                                }
                            }}
                        >
                            {statusMessage.message}
                        </Alert>
                    )}

                    <form onSubmit={formik.handleSubmit}>
                                <Box>
                            {!showPinInput ? (
                                                <TextField
                                                    fullWidth
                                    label="Email Address"
                                                    name="email"
                                                    type="email"
                                                    value={formik.values.email}
                                                    onChange={formik.handleChange}
                                                    error={formik.touched.email && Boolean(formik.errors.email)}
                                                    helperText={formik.touched.email && formik.errors.email}
                                                    disabled={isLoading}
                                                    required
                                    InputLabelProps={{ 
                                        sx: { 
                                            color: 'rgba(255,255,255,0.7)',
                                            '&.Mui-focused': {
                                                color: '#60A5FA'
                                            },
                                            '&.MuiFormLabel-filled': {
                                                color: 'rgba(255,255,255,0.7)'
                                            },
                                            background: 'rgba(10, 20, 41, 0.95)',
                                            padding: '0 8px',
                                            marginLeft: '-4px'
                                        },
                                        shrink: true
                                    }}
                                                    InputProps={{ 
                                                        style: { color: 'white' },
                                        sx: {
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(96,165,250,0.3)',
                                                borderWidth: '1px',
                                                transition: 'all 0.2s ease'
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#60A5FA',
                                                borderWidth: '1px'
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#60A5FA',
                                                borderWidth: '2px'
                                            },
                                            borderRadius: '12px',
                                            backgroundColor: 'rgba(255,255,255,0.03)',
                                            backdropFilter: 'blur(10px)',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255,255,255,0.05)'
                                            },
                                            '&.Mui-focused': {
                                                backgroundColor: 'rgba(255,255,255,0.05)'
                                            }
                                        }
                                    }}
                                />
                            ) : (
                                <Box>
                                    <Box sx={{ 
                                        textAlign: 'center', 
                                        mb: 3,
                                        p: 3,
                                        bgcolor: 'rgba(96,165,250,0.05)',
                                        borderRadius: '12px',
                                        border: '1px solid rgba(96,165,250,0.1)'
                                    }}>
                                        <Typography 
                                            variant="body1" 
                                            color="rgba(255,255,255,0.7)" 
                                            gutterBottom
                                            sx={{ fontSize: '0.95rem' }}
                                        >
                                            We sent a PIN to
                                        </Typography>
                                        <Typography 
                                            variant="h6" 
                                            sx={{ 
                                                color: '#60A5FA',
                                                fontWeight: 600,
                                                letterSpacing: '0.5px'
                                            }}
                                        >
                                            {formik.values.email}
                                        </Typography>
                                    </Box>
                                                <TextField
                                                    fullWidth
                                        label="Enter PIN"
                                        name="pin"
                                        value={formik.values.pin}
                                                    onChange={formik.handleChange}
                                        error={formik.touched.pin && Boolean(formik.errors.pin)}
                                        helperText={formik.touched.pin && formik.errors.pin}
                                                    disabled={isLoading}
                                                    required
                                        InputLabelProps={{ 
                                            sx: { 
                                                color: 'rgba(255,255,255,0.7)',
                                                '&.Mui-focused': {
                                                    color: '#60A5FA'
                                                },
                                                '&.MuiFormLabel-filled': {
                                                    color: 'rgba(255,255,255,0.7)'
                                                },
                                                background: 'rgba(10, 20, 41, 0.95)',
                                                padding: '0 8px',
                                                marginLeft: '-4px'
                                            },
                                            shrink: true
                                        }}
                                        InputProps={{
                                            style: { 
                                                color: 'white', 
                                                letterSpacing: '0.5em', 
                                                fontSize: '1.5rem',
                                                textAlign: 'center'
                                            },
                                            sx: {
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'rgba(96,165,250,0.3)',
                                                    borderWidth: '1px'
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#60A5FA',
                                                    borderWidth: '1px'
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#60A5FA',
                                                    borderWidth: '2px'
                                                },
                                                borderRadius: '12px',
                                                backgroundColor: 'rgba(255,255,255,0.03)',
                                                backdropFilter: 'blur(10px)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255,255,255,0.05)'
                                                },
                                                '&.Mui-focused': {
                                                    backgroundColor: 'rgba(255,255,255,0.05)'
                                                }
                                            }
                                        }}
                                    />
                                        </Box>
                                    )}

                            <Button
                                fullWidth
                                variant="contained"
                                type="submit"
                                disabled={isLoading}
                                sx={{
                                    mt: 3,
                                    bgcolor: '#60A5FA',
                                    background: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
                                    py: 1.8,
                                    textTransform: 'none',
                                    fontSize: '1.1rem',
                                    fontWeight: 500,
                                    borderRadius: '12px',
                                    boxShadow: '0 4px 20px 0 rgba(96,165,250,0.3)',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                                        boxShadow: '0 6px 25px 0 rgba(96,165,250,0.4)'
                                    },
                                    '&.Mui-disabled': {
                                        bgcolor: 'rgba(96,165,250,0.2)'
                                    },
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                {isLoading ? (
                                    <CircularProgress size={24} sx={{ color: 'white' }} />
                                ) : (
                                    showPinInput ? 'Verify PIN' : 'Continue with Email'
                                )}
                            </Button>

                    <Divider sx={{ 
                                my: 4, 
                                color: 'rgba(255,255,255,0.5)',
                        '&::before, &::after': {
                            borderColor: 'rgba(255,255,255,0.1)'
                                },
                                fontSize: '0.9rem',
                                letterSpacing: '1px'
                            }}>
                                or
                            </Divider>

                            <Box sx={{ 
                                position: 'relative',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    inset: 0,
                                    borderRadius: '12px',
                                    padding: '1px',
                                    background: 'linear-gradient(135deg, rgba(96,165,250,0.5), rgba(59,130,246,0.5))',
                                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                    WebkitMaskComposite: 'xor',
                                    maskComposite: 'exclude'
                                }
                            }}>
                                <GoogleLogin
                                    onSuccess={handleGoogleSuccess}
                                    onError={handleGoogleError}
                                    theme="filled_blue"
                                    shape="rectangular"
                                    size="large"
                                    width="100%"
                                    text="continue_with"
                                    locale="en"
                                />
                            </Box>
                        </Box>
                    </form>
                </DialogContent>
            </Box>
        </Dialog>
    );
};

export default LoginRegisterModal;
